import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FaqSection = ({ userRole }) => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('general');

  // FAQ Categories
  const faqCategories = [
    { id: 'general', label: 'Général', icon: 'HelpCircle' },
    { id: 'account', label: 'Compte', icon: 'User' },
    { id: 'campaigns', label: 'Campagnes', icon: 'Target' },
    { id: 'commissions', label: 'Commissions', icon: 'DollarSign' },
    { id: 'technical', label: 'Technique', icon: 'Settings' },
    { id: 'billing', label: 'Facturation', icon: 'CreditCard' }
  ];

  // FAQ Data organized by categories
  const faqData = {
    general: [
      {
        question: 'Qu\'est-ce qu\'Artis.io et comment ça fonctionne ?',
        answer: 'Artis.io est une plateforme de marketing d\'affiliation spécialisée dans l\'industrie musicale. Elle connecte les artistes, venues, agents et apporteurs d\'affaires pour optimiser les collaborations et les revenus. La plateforme utilise un système de liens de suivi pour mesurer les performances et calculer les commissions automatiquement.'
      },
      {
        question: 'Comment puis-je commencer à utiliser Artis.io ?',
        answer: 'Pour commencer, inscrivez-vous en choisissant votre rôle (agent, venue, artiste, etc.), complétez votre profil, vérifiez votre email, puis explorez le tableau de bord adapté à votre rôle. Des tutoriels guidés sont disponibles pour chaque type d\'utilisateur.'
      },
      {
        question: 'Quels sont les différents types d\'utilisateurs sur la plateforme ?',
        answer: 'Artis.io supporte 5 types d\'utilisateurs : les Agents (gestion de campagnes), les Apporteurs d\'affaires (développement réseau), les Venues (gestion d\'espaces), les Artistes (promotion et booking), et les Administrateurs (supervision globale).'
      },
      {
        question: 'La plateforme est-elle disponible en français ?',
        answer: 'Oui, Artis.io est entièrement disponible en français, incluant l\'interface utilisateur, la documentation, et le support client. Nous proposons également des guides spécifiques au marché français.'
      }
    ],
    account: [
      {
        question: 'Comment modifier mes informations de profil ?',
        answer: 'Accédez à la section "Profil" depuis le menu principal, cliquez sur "Modifier", mettez à jour vos informations, puis sauvegardez. Certaines modifications peuvent nécessiter une vérification supplémentaire.'
      },
      {
        question: 'Comment changer mon mot de passe ?',
        answer: 'Dans les paramètres de compte, sélectionnez "Sécurité", cliquez sur "Changer le mot de passe", entrez votre mot de passe actuel et le nouveau, puis confirmez. Un email de confirmation sera envoyé.'
      },
      {
        question: 'Puis-je avoir plusieurs rôles sur mon compte ?',
        answer: 'Actuellement, chaque compte est associé à un seul rôle principal. Si vous avez besoin de plusieurs rôles, contactez notre support pour discuter des options disponibles.'
      },
      {
        question: 'Comment supprimer définitivement mon compte ?',
        answer: 'La suppression de compte peut être demandée via les paramètres de compte ou en contactant le support. Attention : cette action est irréversible et toutes vos données seront perdues.'
      }
    ],
    campaigns: [
      {
        question: 'Comment créer ma première campagne marketing ?',
        answer: 'Allez dans "Générateur de Liens", cliquez sur "Nouvelle Campagne", définissez vos objectifs, configurez votre audience cible, choisissez vos paramètres de suivi, puis lancez la campagne. Un assistant vous guide étape par étape.'
      },
      {
        question: 'Comment suivre les performances de mes campagnes ?',
        answer: 'Utilisez la section "Analytics de Liens" pour voir en temps réel les clics, conversions, et revenus générés. Vous pouvez filtrer par période, source, ou type de campagne pour analyser les performances.'
      },
      {
        question: 'Puis-je modifier une campagne en cours ?',
        answer: 'Oui, vous pouvez modifier certains aspects d\'une campagne active comme le budget, les audiences cibles, ou les paramètres de suivi. Les modifications importantes peuvent nécessiter une revalidation.'
      },
      {
        question: 'Quelle est la différence entre une campagne publique et privée ?',
        answer: 'Les campagnes publiques sont visibles par tous les partenaires éligibles, tandis que les campagnes privées ne sont accessibles qu\'aux partenaires spécifiquement invités ou selon des critères définis.'
      }
    ],
    commissions: [
      {
        question: 'Comment sont calculées les commissions ?',
        answer: 'Les commissions sont calculées automatiquement basées sur les performances mesurées via les liens de suivi. Le taux dépend de votre accord de partenariat et du type d\'action (clic, conversion, vente). Le calcul est transparent et vérifiable dans votre tableau de bord.'
      },
      {
        question: 'Quand recevrai-je mes paiements de commissions ?',
        answer: 'Les paiements sont traités mensuellement pour les commissions validées. Le délai de paiement standard est de 30 jours après la fin du mois de performance, sous réserve de validation des métriques.'
      },
      {
        question: 'Puis-je contester une commission calculée ?',
        answer: 'Oui, vous pouvez ouvrir un litige dans la section "Suivi des Commissions". Fournissez les détails de votre contestation avec les preuves nécessaires. Notre équipe examinera le cas sous 5 jours ouvrables.'
      },
      {
        question: 'Quels sont les seuils minimum pour les paiements ?',
        answer: 'Le seuil minimum de paiement est de 50€ pour les virements bancaires et 20€ pour les portefeuilles électroniques. Les commissions inférieures au seuil sont reportées au mois suivant.'
      }
    ],
    technical: [
      {
        question: 'Comment intégrer l\'API Artis.io dans mon système ?',
        answer: 'Consultez notre documentation API complète dans la section "Documentation API". Vous y trouverez les endpoints, exemples de code, et guides d\'intégration. Un token d\'authentification est requis pour accéder aux APIs.'
      },
      {
        question: 'Pourquoi mes liens de suivi ne fonctionnent pas ?',
        answer: 'Vérifiez que le lien est correctement formaté, que la campagne est active, et que les paramètres UTM sont configurés. Si le problème persiste, consultez les logs d\'erreur ou contactez le support technique.'
      },
      {
        question: 'Comment résoudre les problèmes de connexion ?',
        answer: 'Vérifiez votre connexion internet, videz le cache de votre navigateur, désactivez temporairement les bloqueurs de publicité, ou essayez un navigateur différent. Pour les problèmes persistants, contactez le support.'
      },
      {
        question: 'Artis.io est-il compatible avec tous les navigateurs ?',
        answer: 'Artis.io est optimisé pour Chrome, Firefox, Safari et Edge (versions récentes). Pour une expérience optimale, nous recommandons de maintenir votre navigateur à jour.'
      }
    ],
    billing: [
      {
        question: 'Comment fonctionne la facturation sur Artis.io ?',
        answer: 'Artis.io utilise un modèle de commission sur performance. Aucun frais d\'abonnement mensuel n\'est requis. Nous prélevons un pourcentage sur les commissions générées selon les termes de votre accord de partenariat.'
      },
      {
        question: 'Puis-je obtenir une facture pour mes paiements ?',
        answer: 'Oui, toutes les transactions génèrent automatiquement des factures détaillées disponibles dans votre espace de facturation. Vous pouvez les télécharger en PDF ou les recevoir par email.'
      },
      {
        question: 'Quels moyens de paiement acceptez-vous ?',
        answer: 'Nous acceptons les virements bancaires SEPA, PayPal, Stripe, et certains portefeuilles électroniques. Les méthodes disponibles peuvent varier selon votre localisation et type de compte.'
      },
      {
        question: 'Comment modifier mes informations de facturation ?',
        answer: 'Dans les paramètres de compte, section "Facturation", vous pouvez mettre à jour vos informations de paiement, adresse de facturation, et préférences de réception des factures.'
      }
    ]
  };

  const toggleFaqExpansion = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const currentFaqs = faqData?.[selectedCategory] || [];

  return (
    <div className="space-y-8">
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="HelpCircle" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-card-foreground">Questions Fréquemment Posées</h2>
            <p className="text-muted-foreground">Trouvez rapidement les réponses à vos questions</p>
          </div>
        </div>

        {/* Category Selector */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {faqCategories?.map((category) => (
              <button
                key={category?.id}
                onClick={() => setSelectedCategory(category?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category?.id
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                }`}
              >
                <Icon name={category?.icon} size={16} />
                <span>{category?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {currentFaqs?.map((faq, index) => (
            <div key={index} className="border border-border rounded-lg">
              <button
                onClick={() => toggleFaqExpansion(index)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
              >
                <h3 className="text-base font-medium text-card-foreground pr-4">{faq?.question}</h3>
                <Icon 
                  name={expandedFaq === index ? "ChevronUp" : "ChevronDown"} 
                  size={20} 
                  className="text-muted-foreground flex-shrink-0" 
                />
              </button>

              {expandedFaq === index && (
                <div className="px-4 pb-4">
                  <div className="pt-4 border-t border-border">
                    <p className="text-muted-foreground leading-relaxed">{faq?.answer}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Help Actions */}
        <div className="mt-8 p-4 bg-muted/50 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div>
              <h3 className="text-lg font-semibold text-card-foreground mb-1">
                Vous ne trouvez pas votre réponse ?
              </h3>
              <p className="text-sm text-muted-foreground">
                Notre équipe support est là pour vous aider 24h/24, 7j/7
              </p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline">
                <Icon name="MessageSquare" size={16} className="mr-2" />
                Chat Support
              </Button>
              <Button variant="default">
                <Icon name="Mail" size={16} className="mr-2" />
                Contacter Support
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Popular Articles */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Articles Populaires</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer">
            <Icon name="TrendingUp" size={16} className="text-accent" />
            <div>
              <div className="text-sm font-medium text-card-foreground">Optimiser ses campagnes</div>
              <div className="text-xs text-muted-foreground">Guide complet</div>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer">
            <Icon name="DollarSign" size={16} className="text-success" />
            <div>
              <div className="text-sm font-medium text-card-foreground">Maximiser ses revenus</div>
              <div className="text-xs text-muted-foreground">Stratégies avancées</div>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer">
            <Icon name="Link" size={16} className="text-warning" />
            <div>
              <div className="text-sm font-medium text-card-foreground">Gestion des liens</div>
              <div className="text-xs text-muted-foreground">Bonnes pratiques</div>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer">
            <Icon name="BarChart" size={16} className="text-error" />
            <div>
              <div className="text-sm font-medium text-card-foreground">Analytics avancées</div>
              <div className="text-xs text-muted-foreground">Interpréter les données</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqSection;